<template>
	<div :class="classes" v-if="thePages > 1">
		<span @click="loadMore" v-if="doLoadMore && page < thePages" class="load-more button" v-html="loadMoreLabelCompd" />

		<div class="pagination-wrap" v-if="!doLoadMore">
			<span class="pagination double-left arrows"
				><span
					@click="changePage('start', page === 1)"
					:class="[{ arrows: true, start: true, disabled: page === 1, clickable: page !== 1 }, pageClasses(1)]"
					>&lt;&lt;</span
				></span
			><span class="pagination single-left arrows"
				><span
					@click="changePage('previous', page === 1)"
					:class="[{ arrows: true, previous: true, disabled: page === 1, clickable: page !== 1 }, pageClasses(page - 1)]"
					>&lt;</span
				></span
			><span v-for="(pageNumber, pageNumberIndex) in pageNumbers" :key="'t' + pageNumberIndex" class="pagination"
				><span v-if="pageNumber === 'break'" class="break">...</span
				><span v-if="isNaN(pageNumber) === false && page === pageNumber && pageNumber > 0" class="current">{{ pageNumber }}</span
				><span
					v-if="isNaN(pageNumber) === false && page !== pageNumber && pageNumber > 0"
					@click="changePage(pageNumber)"
					:class="[{ clickable: true }, pageClasses(pageNumber)]"
					>{{ pageNumber }}</span
				></span
			><span class="pagination single-right arrows"
				><span
					@click="changePage('next', thePages === page)"
					:class="[{ arrows: true, disabled: thePages === page, next: true, clickable: thePages !== page }, pageClasses(page + 1)]"
					>&gt;</span
				></span
			><span class="pagination double-right arrows"
				><span
					@click="changePage('end', thePages === page)"
					:class="[{ arrows: true, end: true, disabled: thePages === page, clickable: thePages !== page }, pageClasses(thePages)]"
					>&gt;&gt;</span
				></span
			>
		</div>
	</div>
</template>

<script>
	export default {
		name: "Pagination",
		props: {
			thePages: {
				type: Number,
			},
			pageClassRules: {
				type: Array,
				default: () => [],
			},
			thePage: {
				type: Number,
			},
			page: {
				type: Number,
			},
			isMobile: {
				type: Boolean,
				default: false,
			},
			colour: {
				type: String,
			},
			doLoadMore: {
				type: Boolean,
				default: false,
			},
			loadMoreLabel: {
				type: String,
				default: "Load more",
			},
			loadingText: {
				type: String,
				default: "Loading...",
			},
			loading: {
				type: Boolean,
				default: false,
			},
		},
		data() {
			return {};
		},
		computed: {
			pageNumbers() {
				const shownPages = 5;
				const result = [];
				const halfShown = Math.floor(shownPages / 2);

				let start = Math.max(1, this.thePage - halfShown);
				let end = Math.min(this.thePages, start + shownPages - 1);

				// Adjust start if we're near the end
				if (end === this.thePages) {
					start = Math.max(1, end - shownPages + 1);
				}

				// Add page numbers
				for (let i = start; i <= end; i++) {
					result.push(i);
				}

				// Add ellipsis and first/last page if necessary
				if (start > 1) {
					result.unshift(1);
					if (start > 2) result.splice(1, 0, 'break');
				}
				if (end < this.thePages) {
					if (end < this.thePages - 1) result.push('break');
					result.push(this.thePages);
				}

				// Add navigation buttons
				if (this.thePage > 1) {
					result.unshift('previous');
					result.unshift('start');
				}
				if (this.thePage < this.thePages) {
					result.push('next');
					result.push('end');
				}

				return result;
			},
			classes() {
				let classes = {
					pagination: true,
					isMobile: this.isMobile,
				};

				if (this.colour) classes[this.colour] = true;

				return classes;
			},
		},
		methods: {
			pageClasses(page) {
				let classes = {};

				this.pageClassRules.forEach((rule) => {
					if (rule.from >= page && rule.to <= page) {
						classes[rule.class] = true;
					}
				});

				return classes;
			},

			changePage(pageNumber, disabled) {
				if (typeof disabled !== "undefined" && disabled === true) return;

				if (pageNumber === "start") pageNumber = 1;
				if (pageNumber === "end") pageNumber = this.thePages;
				if (pageNumber === "previous") pageNumber = this.thePage - 1;
				if (pageNumber === "next") pageNumber = this.thePage + 1;

				this.$emit("changePage", pageNumber);
			},
		},
	};
</script>
<style lang="less" scoped>
	@import "./../../assets/css/less/_helper.less";
	@import "./../../assets/css/less/_settings.less";
	.pagination {
		text-align: center;
		background-color: #fff;
		text-align: center;

		span {
			&:not(.button) {
				padding: 0;
			}
			&.button {
				margin: 20px 0;
			}
		}

		.pagination-wrap {
			margin-top: 8px;
			margin-bottom: 8px;
			display: inline-block;

			> span {
				&:not(.button) {
					padding: 0 2px;
				}

				span {
					background: white;
					border-radius: 5px;
					padding: 6px;
					display: inline-block;
					min-width: 34px;
					margin: 2px 0;
					.trans;

					&.start,
					&.end,
					&.previous,
					&.next {
						margin: 0;
						padding: 8px;
						min-width: 38px;
					}

					&.disabled {
						cursor: default;
						.opacity(0.7);
					}

					&.break {
						cursor: default;
					}

					&.red {
						color: @red;
					}

					&.clickable {
						cursor: pointer;

						&:hover {
							background: @darkContrast;
							color: #fff;

							&.red {
								background: @red;
								color: #fff;
							}
						}
					}

					&.current {
						background: @darkContrast;
						color: #fff;

						&.red {
							background: @red;
							color: #fff;
						}
					}
				}
			}
		}

		&.isMobile {
			.pagination-wrap {
				position: relative;
				padding-top: 44px;
				width: 100%;

				.pagination {
					&.arrows {
						position: absolute;
						top: 0;

						&.double-left {
							left: 0;
						}

						&.single-left {
							left: 40px;
						}

						&.double-right {
							right: 0;
						}

						&.single-right {
							right: 40px;
						}
					}
				}
			}
		}

		&.red {
			.pagination-wrap {
				> span {
					span {
						color: @red;

						&.clickable {
							&:hover {
								background: @red;
							}
						}

						&.current {
							background: @red;
							color: #fff;
						}
					}
				}
			}
		}
	}
</style>
